import { skipToken } from '@apollo/client/react'
import { useRef, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCopyToClipboard, useDebounce } from 'react-use'
import FluentClock20Regular from '~icons/fluent/clock-20-regular'
import FluentCloudArrowUp20Regular from '~icons/fluent/cloud-arrow-up-20-regular'
import FluentDismissCircle20Regular from '~icons/fluent/dismiss-circle-20-regular'
import FluentDocument20Regular from '~icons/fluent/document-20-regular'
import FluentDocumentError20Regular from '~icons/fluent/document-error-20-regular'
import FluentMoreVertical20Regular from '~icons/fluent/more-vertical-20-regular'
import FluentSearch20Regular from '~icons/fluent/search-20-regular'
import FluentSpinnerIos20Filled from '~icons/fluent/spinner-ios-20-filled'
import CursorPagingButton from 'src/components/CursorPaging/CursorPagingButton'
import { dialogInspection } from 'src/components/Modal/InspectDialog'
import { UIButton } from 'src/components/ui/Button'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import { useUserProfileContext } from 'src/components/UserProfile/UserProfile.context'
import {
  executeDeleteFile,
  useGetFileStatistics,
  useListFiles,
} from 'src/services/apollo/file'
import type { FileUploadRecordBaseFragment } from 'src/services/apollo/generated/graphql'
import {
  blobSha256Base64,
  uploadFileMultiPart,
} from 'src/services/helpers/uploadMultipartFile'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { formatBytes } from 'src/utils/formatNum'
import { getNodesFromConnection } from 'src/utils/graphql'

const PAGE_SIZE = 30

export default function ProfileFilesTab() {
  const { user, isMyProfile } = useUserProfileContext()

  const [keyword, setKeyword] = useState('')
  const [pageArgs, setPageArgs] = useState<
    { before?: string; last: number } | { after?: string; first: number }
  >({ last: PAGE_SIZE })
  const {
    data: res,
    loading,
    refetch: reloadFiles,
  } = useListFiles(
    user
      ? {
          userId: user.id,
          q: keyword || undefined,
          ...pageArgs,
        }
      : skipToken,
  )
  const files = getNodesFromConnection(res).reverse()
  const pageInfo = res?.pageInfo
  const havingPrevPage = Boolean(pageInfo?.hasPreviousPage)
  const havingNextPage = Boolean(pageInfo?.hasNextPage)
  const goPrevPage = () =>
    havingPrevPage &&
    setPageArgs({ before: pageInfo?.startCursor, last: PAGE_SIZE })
  const goNextPage = () =>
    havingNextPage &&
    setPageArgs({ after: pageInfo?.endCursor, first: PAGE_SIZE })

  const { data: stats } = useGetFileStatistics(
    user ? { userId: user.id } : skipToken,
  )

  return (
    <>
      <div className="mb-4 grid grid-cols-[1fr_auto] msm:grid-cols-[1fr_auto_1fr] items-center gap-4 text-sm">
        <SearchInput className="justify-self-start" onChange={setKeyword} />
        <p className="max-msm:row-start-2 justify-self-start relative flex items-center text-sm text-neutral-600 dark:text-neutral-500">
          {Boolean(stats?.count) && (
            <>
              <Text
                i18nKey="common:file.counter"
                values={{ count: stats!.count }}
              />{' '}
              ({formatBytes(Number(stats!.size))})
            </>
          )}
          <span className="absolute left-full top-0 translate-x-1 flex items-center w-0 h-full">
            <FluentSpinnerIos20Filled
              className={cx(
                'flex-none size-5 text-black dark:text-white animate-spin',
                !loading && 'invisible',
              )}
            />
          </span>
        </p>
        {isMyProfile && <UploadButton onUpload={reloadFiles} />}
      </div>
      <div className="grid grid-cols-[1fr_auto] msm:grid-cols-[1fr_auto_auto_auto] mmd:grid-cols-[1fr_auto_auto_auto] border-t border-b border-black/15 dark:border-white/15 divide-y divide-black/15 dark:divide-white/15">
        {files.length ? (
          files.map(rec => (
            <FileListItem key={rec.id} file={rec} onUpdate={reloadFiles} />
          ))
        ) : (
          <p className="col-span-full p-3 text-sm text-center">
            <Text i18nKey="common:file.label" values={{ count: 0 }}></Text>
          </p>
        )}
      </div>
      <div className="mt-4 max-mmd:mb-4 flex justify-center">
        <CursorPagingButton
          isPosSeq
          pageInfo={{
            hasPreviousPage: havingNextPage,
            hasNextPage: havingPrevPage,
          }}
          onClickPrev={goNextPage}
          onClickNext={goPrevPage}
        />
      </div>
    </>
  )
}

function SearchInput(
  props: StyleProps & { onChange?: (value: string) => void },
) {
  const { t } = useTranslation(['common'])
  const [inputValue, setInputValue] = useState('')

  useDebounce(
    () => {
      props.onChange?.(inputValue.trim())
    },
    200,
    [props.onChange, inputValue],
  )

  return (
    <label
      data-empty={inputValue ? null : ''}
      className={cx(
        'group inline-grid grid-cols-[32px_1fr_32px] h-8 bg-white dark:bg-zinc-800 rounded-md ring-1 focus-within:ring-2 ring-black/10 dark:ring-black/20 focus-within:ring-black dark:focus-within:ring-white/80 shadow',
        props.className,
      )}
      style={props.style}
    >
      <FluentSearch20Regular className="place-self-center size-5" />
      <input
        type="text"
        value={inputValue}
        placeholder={t('common:file.actions.search.placeholder')}
        className="py-1 bg-transparent outline-none"
        onChange={ev => {
          setInputValue(ev.target.value)
        }}
      />
      <button
        className="inline-grid place-items-center size-full group-data-[empty]:invisible"
        onClick={() => setInputValue('')}
      >
        <FluentDismissCircle20Regular className="size-5" />
      </button>
    </label>
  )
}

function UploadButton(props: { onUpload?: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<number>()

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={async ev => {
          const file = ev.target.files?.[0]
          if (!file) return
          try {
            setProgress(0)
            const hash = await blobSha256Base64(file)
            await uploadFileMultiPart(file, hash, {
              scope: 'custom',
              onProgress: setProgress,
            })
            props.onUpload?.()
          } catch (err: unknown) {
            console.error(err)
            toast.error(String(err))
          } finally {
            setProgress(undefined)
            ev.target.value = ''
          }
        }}
      />
      <UIButton
        variant="outlined"
        disabled={progress != null}
        data-state={progress == null ? 'idle' : 'uploading'}
        icon={
          progress == null ? (
            <FluentCloudArrowUp20Regular className="size-5" />
          ) : (
            <FluentSpinnerIos20Filled className="size-5 animate-spin" />
          )
        }
        className="group justify-self-end"
        onPress={() => {
          fileInputRef.current?.click()
        }}
      >
        <div className="grid grid-rows-1 grid-cols-1">
          <span className="row-start-1 col-start-1 group-data-[state=uploading]:invisible">
            <Text i18nKey="market:train-lora.add-image.upload-file.label" />
          </span>
          <span className="row-start-1 col-start-1 group-data-[state=idle]:invisible">
            {progress == null ? (
              <Text i18nKey="market:train-lora.add-image.upload-file.label" />
            ) : (
              <>
                <Text i18nKey="market:submit.notice.uploading-file" />
                {` (${Math.round(progress * 100)}%)`}
              </>
            )}
          </span>
        </div>
      </UIButton>
    </>
  )
}

function FileListItem(props: {
  file: FileUploadRecordBaseFragment
  onUpdate?: () => void
}) {
  const { beingAdmin } = useMe()
  const hash = props.file.sha256
  const hashShort = hash?.slice(0, 8)

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center gap-x-3 py-3 text-sm">
      <span
        className={cx(
          'inline-flex items-center',
          !props.file.isFinished && 'text-red-600 dark:text-red-500',
        )}
      >
        {props.file.isFinished ? (
          <FluentDocument20Regular className="flex-none mr-1 size-5" />
        ) : (
          <FluentDocumentError20Regular className="flex-none mr-1 size-5" />
        )}
        {props.file.filename}
      </span>
      <p className="row-start-2 col-span-full flex msm:contents items-center gap-4 pl-6 text-neutral-500">
        {beingAdmin && (
          <code
            className="text-xs"
            onClick={() => window.prompt('Full hash', hash)}
          >
            {hashShort}
          </code>
        )}
        <span className="inline-flex items-center">
          <FluentClock20Regular className="flex-none mr-1 size-5" />
          <time
            dateTime={dayjs(props.file.createdAt).toISOString()}
            className="max-msm:text-neutral-500"
          >
            {dayjs(props.file.createdAt).format('lll')}
          </time>
        </span>
      </p>
      <FileListItemButton
        file={props.file}
        onAction={action => {
          switch (action) {
            case 'delete':
              props.onUpdate?.()
              break
          }
        }}
      />
    </div>
  )
}

function FileListItemButton(props: {
  file: FileUploadRecordBaseFragment
  onAction?: (action: string) => void
}) {
  const { beingAdmin } = useMe()
  const [, copy] = useCopyToClipboard()

  return (
    <UIMenuTrigger>
      <AriaButton
        className={cx(
          'inline-grid place-content-center w-full aspect-square p-1.5 hover:bg-black/10 dark:hover:bg-white/10 rounded-full',
          'pressed:text-white dark:pressed:text-black',
          'pressed:bg-black dark:pressed:bg-white',
        )}
      >
        <FluentMoreVertical20Regular className="size-5" />
      </AriaButton>
      <UIMenu placement="bottom">
        {beingAdmin && (
          <UIMenuItem onAction={() => dialogInspection(props.file)}>
            Inspect
          </UIMenuItem>
        )}
        <UIMenuItem
          onAction={async () => {
            copy(props.file.id)
            toast.success(
              <Text i18nKey="file.actions.copy-file-id.messages.success" />,
            )
          }}
        >
          <Text i18nKey="file.actions.copy-file-id.label" />
        </UIMenuItem>
        <UIMenuItem
          className="text-red-600 dark:text-red-500"
          onAction={async () => {
            await executeDeleteFile({ id: props.file.id })
            props.onAction?.('delete')
          }}
        >
          <Text i18nKey="action:delete.label" />
        </UIMenuItem>
      </UIMenu>
    </UIMenuTrigger>
  )
}
